import { hasArrayValues } from "@theme/utils";

import type { CSSProperties, KeyboardEvent } from "react";

/**
 * Spread onto GatsbyImage/StrapiBackgroundImage inside a `display: grid` parent to prevent
 * iOS Safari scroll flicker. Resets GatsbyImage's injected positioning so the image joins the
 * grid flow (`gridArea: "1 / 1"`) instead of forming its own compositor layer. `loading: "eager"`
 * and `transition: "none"` suppress the fade-in that triggers a compositor-layer flash on scroll.
 */
export const gridBackgroundImageProps = {
  loading: "eager" as const,
  imgStyle: { transition: "none" } as CSSProperties,
  style: {
    gridArea: "1 / 1",
    position: "relative",
    zIndex: "auto",
    top: "auto",
    left: "auto",
  } as CSSProperties,
};

/**
 * Handles a keyboard event and calls an action if the event key matches the target key(s).
 *
 * @param event - The keyboard event object.
 * @param action - The function to call when the target key(s) are pressed.
 * @param targetKey - The key(s) that should trigger the action. Defaults to 'Enter' and ' ' (space).
 * @param shouldPreventDefault - Whether to call preventDefault when the key matches. Defaults to true.
 */
export const onKeyDown = (
  event: KeyboardEvent<HTMLElement>,
  action: () => void,
  targetKey?: string | string[],
  shouldPreventDefault = true,
) => {
  targetKey = targetKey ?? ["Enter", " "];

  if (hasArrayValues(targetKey) ? targetKey.includes(event.key) : event.key === targetKey) {
    if (shouldPreventDefault) {
      event.preventDefault();
    }
    action();
  }
};

/**
 * Text To Required Text
 *
 * Some of our form elements need us to add an asterisk to the label if the field is required.
 *
 * @param text
 * @param required
 * @returns {string}
 */
export const markTextAsRequired = (text: string, required: boolean) =>
  required ? `${text}*` : text;

/**
 * Scroll Parent to Child Element
 *
 * This function will use the parent and child elements to determine if the child is in view.
 * If the child is not in view, the parent will be scrolled to show the child.
 * This function is useful for horizontal sliders to ensure the active child is in view.
 *
 * @param parent
 * @param child
 */
export const scrollParentToChildElement = (
  parent: HTMLDivElement | HTMLAnchorElement | HTMLButtonElement,
  child: HTMLDivElement | HTMLAnchorElement | HTMLButtonElement,
) => {
  // Where is the parent on page
  const parentRect = parent.getBoundingClientRect();
  // What can you see?
  const parentViewableArea = {
    height: parent.clientHeight,
    width: parent.clientWidth,
  };
  // Where is the child
  const childRect = child.getBoundingClientRect();
  // Is the child viewable?
  const isViewable =
    childRect.left >= parentRect.left &&
    childRect.right <= parentRect.left + parentViewableArea.width;

  // if you can't see the child try to scroll parent
  if (!isViewable) {
    // Should we scroll using top or bottom? Find the smaller ABS adjustment
    const scrollLeft = childRect.left - parentRect.left;
    const scrollRight = childRect.right - parentRect.right;
    if (Math.abs(scrollLeft) < Math.abs(scrollRight)) {
      parent.scrollLeft += scrollLeft - childRect.width / 2;
    } else {
      parent.scrollLeft += scrollRight + childRect.width / 2;
    }
  }
};

/**
 * Scroll To Id
 *
 * For accessibility its best to scroll and focus on element
 *
 * Focus is moved unconditionally before scrolling starts, rather than waiting for a
 * scroll listener to detect the animation has settled on an exact pixel offset - that
 * approach silently drops focus whenever the smooth scroll is interrupted or lands a
 * pixel off from the computed target.
 *
 * @param id - elements id to scroll to
 * @param isNavbarHidden - Navigation is disabled
 * @param offset - If we need to scroll lower on page, add offset
 */
export function scrollToId(id: string, isNavbarHidden = false, offset = 0, navHeight?: number) {
  if (typeof document === "undefined") return;
  const element = document.getElementById(id);
  if (!element) return;
  const elemTop = element.getBoundingClientRect().top;
  const expandedNavHeight = window.innerWidth >= 1024 ? 157 : 98;
  const collapsedNavHeight = window.innerWidth >= 1024 ? 115 : 46;
  const computedNavHeight =
    navHeight ?? (elemTop > 0 ? collapsedNavHeight : expandedNavHeight);
  const bannerHeight = isNavbarHidden ? 0 : computedNavHeight + offset;
  const top = elemTop + window.scrollY - bannerHeight;

  const priorTabIndex = element.getAttribute("tabindex");
  element.tabIndex = -1;
  element.focus({ preventScroll: true });
  if (priorTabIndex === null) {
    element.removeAttribute("tabindex");
  } else {
    element.setAttribute("tabindex", priorTabIndex);
  }

  const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  window.scrollTo({
    top,
    behavior: prefersReducedMotion ? "auto" : "smooth",
  });
}
